import { AnimatePresence, motion } from "framer-motion";
import { usePostHog } from "@posthog/react";
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@clerk/react";

import sillLogo from "../assets/sill-logo.svg";
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  IdCard,
  House,
  Menu,
  MessageSquareText,
  Search,
  Settings,
  UserRound,
  X,
} from "lucide-react";

import { SidebarPlusBadgeButton } from "../components/billing/SidebarPlusBadgeButton";
import {
  AdvisorView,
  ProfileView,
} from "../components/dashboard/dashboard-views";
import { HomeView } from "../components/home/HomeView";
import { DocumentsView } from "../components/dashboard/documents/documents-view";
import { useSupport } from "../components/support/support-provider";
import {
  type PropertiesViewIntent,
  PropertiesView,
} from "../components/dashboard/properties/properties-view";
import { RateWatchView } from "../components/dashboard/rate-watch-view";
import { ReadinessPageView } from "../components/readiness/ReadinessPageView";
import { FinishProfileModal } from "../components/dashboard/FinishProfileModal";
import {
  clearAssumedFields,
  pruneAssumedFields,
  mergeAssumedFields,
  readAssumedFields,
} from "../lib/finish-profile";
import {
  ADVISOR_PROMPTS,
  buildDashboardSearchParams,
  buildDashboardSnapshot,
  deriveHighestVerificationTier,
  formatCompactCurrency,
  resolveAdvisorContext,
  resolveAdvisorPanel,
  resolveDashboardView,
  type AdvisorPanel,
  type AdvisorContext,
  type DashboardPrimaryView,
  type DashboardSnapshot,
  type DashboardView,
  type FinanceReadinessPreviewCategory,
  type FinanceReadinessPreviewItem,
  type TrackedProperty,
} from "../lib/dashboard";
import {
  clearOnboardingResult,
  isCompletedOnboardingResult,
  saveOnboardingResult,
} from "../lib/onboarding";
import { fetchCurrentUser } from "../lib/onboarding-api";
import {
  extractFinishProfileAckFromBootstrap,
  extractReferralBannerAckFromBootstrap,
  extractReferralPublishPromptAckFromBootstrap,
  type ReferralBannerAck,
  type ReferralPublishPromptAck,
  extractTourStateFromBootstrap,
  putFinishProfileAck,
  type FinishProfileAckStatus,
} from "../lib/app-state-api";
import type { TourState } from "../components/overview/platform-tour-modal";
// IN_APP_NOTIFICATIONS_FLAG_KEY intentionally not imported: Plan 287 renders the
// bell + rate-watch in-app notifications unconditionally (dev-phase convention).
import type { DashboardJourneyStep } from "../lib/dashboard-milestones";
import { useReadiness } from "../hooks/useReadiness";
import { useAdvisorDrawerStore, useAdvisorStore } from "../stores/advisor";
import { useOpenAdvisorDrawer } from "../hooks/useOpenAdvisorDrawer";
import type {
  PatchProfileResponse,
  UseProfileEditOptions,
} from "../lib/profile/use-profile-edit";
import { persistProfilePatchResponse } from "../lib/profile/persist-profile-patch-response";
import type {
  TargetEditPatch,
  UseTargetEditOptions,
} from "../lib/profile/use-target-edit";
import {
  listSavedProperties,
  previewSavedPropertiesReadiness,
  type SavedProperty,
  type SavedPropertyReadinessPreview,
} from "../lib/properties";
import {
  displayReadinessTargetArea,
  normalizeReadinessTargetLabel,
  snapshotWithActiveTargetDisplay,
} from "../lib/readiness-target-display";
import { getReadinessScoreAppearance } from "../lib/readiness-score-appearance";
import type {
  ReadinessFactorID,
  ReadinessResult,
  ReadinessTarget,
  ReadinessTimeline,
} from "../lib/readiness-types";
import { cn } from "../lib/utils";
import { Divider } from "../components/ui/divider";
import { Spinner } from "../components/ui/feedback";
import { TierBadge } from "../components/ui/tier-badge";
import { useVerifiedStore } from "../stores/verified";
import { OverviewSkeleton } from "../components/ui/skeleton";
import { useToast } from "../components/ui/toast";
import { useDataReady } from "../hooks/useDataReady";
import { useMarketplaceConnection } from "../hooks/useMarketplaceConnection";
import { WaitlistPositionCardContainer } from "../components/marketplace/WaitlistPositionCardContainer";
import { ReferralBannerContainer } from "../components/referral/referral-banner";
import { ReferralPublishPromptContainer } from "../components/referral/referral-publish-prompt";
import { useWaitlist } from "../hooks/useWaitlist";
import { useDashboardShellCapabilities } from "../hooks/useDashboardShellCapabilities";
import { useMessagingEnabled } from "../hooks/useMessagingEnabled";
import { useMessagesStore } from "../stores/messages";
import { isInFlightOrActive } from "../lib/marketplace-status";
import { marketplaceBrowseHref } from "../lib/marketplace-route";
import { plaidConnectErrorToast } from "../lib/plaid-connect-error-copy";
import { useOffersStore } from "../stores/offers";
import { useNotificationsStore } from "../stores/notifications";
import { DashboardSidebarGrid } from "../components/dashboard/DashboardSidebarGrid";
import { DashboardHeaderBadges } from "../components/dashboard/DashboardHeaderBadges";
import {
  computeSidebarWidth,
  deriveSidebarPassportLiveState,
  filterDashboardNavGroups,
  sidebarPassportCompetingCopy,
  SIDEBAR_PASSPORT_LIVE_STATE_COPY,
  type DashboardNavIcon,
  type DashboardNavItem,
  type ShellActiveView,
} from "./DashboardPage.helpers";

function onboardingStepForFinanceCategory(
  category: FinanceReadinessPreviewCategory,
) {
  switch (category) {
    case "income":
      return "income";
    case "cash-to-close":
      return "savings";
    case "debts":
      return "monthly_debts";
    case "shopping-target":
      return "target_area";
    case "credit-band":
      return "credit_score";
    case "monthly-comfort":
      return "budget";
  }
}

// SILL-173: localStorage key for the rail dismissibility flag. The
// presence of any value (we store an ISO timestamp) means dismissed.
const JOURNEY_RAIL_DISMISSED_STORAGE_KEY = "journey_rail_dismissed_at";

function resolvePropertiesIntentFromSearchParams(
  searchParams: URLSearchParams,
): PropertiesViewIntent {
  if (
    searchParams.get("property_action") === "edit" &&
    searchParams.get("property_id")
  ) {
    return {
      kind: "edit",
      propertyID: searchParams.get("property_id")!,
    };
  }

  return null;
}

export function DashboardPage() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Plan 435 M6 (asd-entry-repoint): the ONE way this page's Ask Sill entry
  // points open the drawer (store action + analytics, paired).
  const openAdvisorDrawer = useOpenAdvisorDrawer();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isMobileScoreOpen, setIsMobileScoreOpen] = useState(false);
  // Post-signup finish-profile nudge (SILL-398): the chat door records the
  // GOOD-DATA fields it assumed at intake. That list goes stale the moment real
  // or verified data lands (doc upload, bank connect, manual edit), so it's
  // reconciled against the settled snapshot below before the modal opens — a
  // complete profile shouldn't be told to "finish" fields it already has.
  const [clientAssumedFields] = useState<string[]>(() => readAssumedFields());
  // In-session hide (instant) for the current tab.
  const [finishProfileDismissed, setFinishProfileDismissed] = useState(false);
  // Durable acknowledgement decoded from /api/users/me app_state (Layer 2):
  // once dismissed/completed on any device, the nudge never re-shows.
  const [finishProfileAck, setFinishProfileAck] =
    useState<FinishProfileAckStatus | null>(null);
  // Referral discovery surfaces: durable dismissal of the dashboard banner and
  // the one-time post-publish prompt marker, both decoded from the same
  // bootstrap app_state map. undefined = bootstrap not yet resolved.
  const [referralBannerAck, setReferralBannerAck] = useState<
    ReferralBannerAck | null | undefined
  >(undefined);
  const [referralPublishPromptAck, setReferralPublishPromptAck] = useState<
    ReferralPublishPromptAck | null | undefined
  >(undefined);
  // Armed by ProfileView's onPublished after a go-live succeeds this session.
  const [justPublished, setJustPublished] = useState(false);
  // While the one-time post-publish prompt is VISIBLE (its onShown → its
  // onDismissed), the passport's referral banner steps aside so the same
  // headline + link isn't stacked twice. Overview keeps its banner. Driven by
  // the prompt's own events, never predicted at publish time.
  const [publishPromptActive, setPublishPromptActive] = useState(false);
  const [isAdvisorRailExpanded, setIsAdvisorRailExpanded] = useState(false);
  const [propertiesIntent, setPropertiesIntent] =
    useState<PropertiesViewIntent>(() =>
      resolvePropertiesIntentFromSearchParams(searchParams),
    );
  const [dashboardRefreshNonce, setDashboardRefreshNonce] = useState(0);
  const [snapshot, setSnapshot] = useState<DashboardSnapshot>(() =>
    buildDashboardSnapshot(),
  );
  const [dashboardBootstrapSettled, setDashboardBootstrapSettled] = useState(
    () => isLoaded && !isSignedIn,
  );
  const [dashboardBootstrapError, setDashboardBootstrapError] =
    useState<Error | null>(null);
  // Bootstrap tour state decoded from /api/users/me app_state (M3).
  // undefined = not yet resolved from bootstrap (initial value, before the fetch).
  // null = bootstrap resolved but no platform_tour.v1 row exists (treated as not_started).
  // TourState = backend row decoded successfully; backend is the source of truth.
  const [bootstrapTourState, setBootstrapTourState] = useState<
    TourState | null | undefined
  >(undefined);
  // SILL-173: rail dismissibility persists in localStorage so it stays
  // hidden across reloads. Overview shows a session-dismissed restore toast.
  const [journeyRailDismissed, setJourneyRailDismissed] = useState<boolean>(
    () => {
      if (typeof window === "undefined") return false;
      try {
        return Boolean(
          window.localStorage.getItem(JOURNEY_RAIL_DISMISSED_STORAGE_KEY),
        );
      } catch {
        return false;
      }
    },
  );
  const handleDismissJourneyRail = useCallback(() => {
    try {
      window.localStorage.setItem(
        JOURNEY_RAIL_DISMISSED_STORAGE_KEY,
        new Date().toISOString(),
      );
    } catch {
      /* noop — purely cosmetic state */
    }
    setJourneyRailDismissed(true);
  }, []);
  const handleRestoreJourneyRail = useCallback(() => {
    try {
      window.localStorage.removeItem(JOURNEY_RAIL_DISMISSED_STORAGE_KEY);
    } catch {
      /* noop */
    }
    setJourneyRailDismissed(false);
  }, []);
  const posthog = usePostHog();
  const { pushToast } = useToast();
  const { feedEnabled, marketplaceEnabled } = useDashboardShellCapabilities();
  const offersCount = useOffersStore((s) => s.count);
  const feedUnreadCount = useNotificationsStore((s) => s.unreadCount);
  // Live marketplace connection: when in-flight/active the passport is off the
  // market (Model A). Drives the Passport "Connected" pill + connected publish
  // state (buttons suppressed) so the FE matches the backend go-live block.
  // Plan 435 M1 (mfh-sidebar-passport-card carryover): `pendingRequest`
  // rides this SAME hook/request now, so the sidebar's "Request pending"
  // state costs no additional read.
  const {
    connection: marketplaceConnection,
    pendingRequest,
    refresh: refreshMarketplaceConnection,
    settled: marketplaceConnectionSettled,
  } = useMarketplaceConnection("agent");
  // Plan 317: queued area (non-launched metro) → relabel the publish block from
  // "Live to agents" to "Waitlisted for {area}". Owner-scoped read; the waitlist
  // card container self-fetches the same cheap endpoint for its own render.
  // Gated on the single marketplace launch flag (SILL-504): the passport view
  // mounts outside MarketplaceRouteGate, so without this a pre-launch buyer
  // would see waitlist marketplace messaging the moment their area is queued.
  const { waitlist: waitlistPosition, refresh: refreshWaitlist } = useWaitlist({
    enabled: marketplaceEnabled,
  });
  const isConnectedToAgent = marketplaceConnection
    ? isInFlightOrActive(marketplaceConnection.status)
    : false;

  useEffect(() => {
    if (searchParams.get("tab") === "agent") {
      // Settings' "Your Team" tab was retired (operator feedback fix) —
      // this legacy deep link now lands on the dedicated /team page.
      navigate("/team", { replace: true });
    }
  }, [navigate, searchParams]);

  const notifyStub = (title: string, description?: string) => {
    posthog?.capture?.("overview_stub_cta_clicked", { title });
    pushToast({ description, title });
  };
  const requestedView = resolveDashboardView(searchParams);
  // feedEnabled guard removed (Plan 287 M3): ?view=feed redirects to /feed.
  // Feed has no sidebar/nav entry — it's reached only via the header bell — so
  // this view is never set from nav; legacy ?view=feed deep links still resolve
  // to the dashboard overview (the standalone /feed route serves the inbox).
  const activeView = requestedView === "feed" ? "overview" : requestedView;
  const usesAbbreviatedSidebar = activeView === "advisor";
  const advisorPanel = resolveAdvisorPanel(searchParams);
  const advisorContext = resolveAdvisorContext(searchParams);
  // SILL-255: readiness lives in a shared module-level store now, so this
  // hook reads the cache without remounting on view changes or page
  // navigation. We pass `getToken` directly — the hook captures it in a ref
  // and ignores identity changes after first mount, so view switches no
  // longer retrigger fetches. Mutation handlers call `refreshLiveData`
  // (below), which routes through `activeReadiness.refresh()`.
  const activeReadiness = useReadiness({
    enabled: isLoaded && !!isSignedIn,
    getToken,
  });
  const displaySnapshot = snapshotWithActiveTargetDisplay(
    snapshot,
    activeReadiness.data?.target ?? null,
  );
  const effectiveDashboardBootstrapSettled =
    isLoaded && !isSignedIn ? true : dashboardBootstrapSettled;
  // Only reconcile against real data: pruning before the snapshot settles would
  // measure preview fallbacks, not the user's actual profile.
  const finishProfileFields = useMemo(() => {
    if (!effectiveDashboardBootstrapSettled) return [];
    const serverAssumedFields =
      activeReadiness.data?.result?.assumed_fields ?? [];
    return pruneAssumedFields(
      mergeAssumedFields(clientAssumedFields, serverAssumedFields),
      displaySnapshot,
    );
  }, [
    effectiveDashboardBootstrapSettled,
    clientAssumedFields,
    activeReadiness.data?.result?.assumed_fields,
    displaySnapshot,
  ]);
  const showFinishProfile =
    !finishProfileDismissed &&
    finishProfileAck === null &&
    finishProfileFields.length > 0;
  // Hide instantly for this tab, then best-effort persist the terminal status
  // so the nudge stays dismissed across reloads and devices (mirrors the tour
  // write — getToken resolves null with no session, in which case we skip).
  const acknowledgeFinishProfile = useCallback(
    (status: FinishProfileAckStatus) => {
      clearAssumedFields();
      setFinishProfileDismissed(true);
      void getToken().then((token) => {
        if (token) void putFinishProfileAck(status, token).catch(() => {});
      });
    },
    [getToken],
  );
  const overviewReady = useDataReady([
    {
      data: effectiveDashboardBootstrapSettled ? displaySnapshot : null,
      error: isSignedIn ? dashboardBootstrapError : null,
      hasSettled: effectiveDashboardBootstrapSettled,
      isLoading:
        isLoaded && !!isSignedIn && !effectiveDashboardBootstrapSettled,
    },
    {
      data: activeReadiness.data,
      enabled: isLoaded && !!isSignedIn,
      error: activeReadiness.error,
      isLoading: activeReadiness.isLoading,
    },
  ]);
  // Bind the sidebar/header overlay to `isLoading` (no data yet), not
  // `isRefreshing`. Background refreshes from mutations stay invisible —
  // the score keeps rendering with the previous value until the new one
  // arrives.
  const isReadinessRefreshing = activeReadiness.isLoading;

  // SILL-187 Phase 8b: mutation hook deps for the Goals column. Memoized
  // so `mutate`'s identity stays stable across renders. Both onSuccess and
  // onStaleRebase nudge the readiness refetch so the page reflects the
  // server's new state. getBaseTargetId reads through the active
  // readiness response; getBaseSnapshotId reads from the snapshot the
  // bootstrap loader populated.
  const activeReadinessTarget = activeReadiness.data?.target ?? null;
  const activeTargetId = activeReadinessTarget?.id ?? null;
  const currentSnapshotId = displaySnapshot.currentSnapshotId ?? null;
  // SILL-255: only mutation handlers call this. Passive navigation (view
  // changes, page entry) must NOT trigger a readiness refetch.
  const refreshReadiness = activeReadiness.refresh;
  const refreshLiveData = useCallback(() => {
    void refreshReadiness();
    setDashboardRefreshNonce((n) => n + 1);
  }, [refreshReadiness]);

  // SILL-255: the end-turn readiness refresh watcher (advisor turn ended →
  // refetch) MOVED to `ConsumerFrame` in the plan 435 M6 integration pass
  // (drawer design doc §4.1) — one watcher over the one shared session now
  // covers a turn in the drawer on ANY route AND a turn on this full page,
  // instead of a copy living here that only ever saw the full page. See
  // `routes/ConsumerFrame.tsx`.

  // SILL-173: optimistically light the `ask-sill` rail step the moment a
  // turn opens. The conversation row is created server-side as the user's
  // message lands, but the bootstrap `/api/users/me` payload that carries
  // `has_conversation` only re-reads when `dashboardRefreshNonce` bumps —
  // and the falling-edge handler above only refreshes readiness, not the
  // bootstrap. Flipping locally on the streaming rising edge avoids an
  // extra GET per turn and gives instant feedback; the next bootstrap
  // refresh will agree.
  useEffect(() => {
    return useAdvisorStore.subscribe((state, prevState) => {
      if (state.isStreaming && !prevState.isStreaming) {
        setSnapshot((current) =>
          current.hasConversation
            ? current
            : { ...current, hasConversation: true },
        );
      }
    });
  }, []);

  const notifyNumbersChanged = useCallback(() => {
    pushToast({ title: "Your numbers may have changed" });
  }, [pushToast]);
  const notifyPlaidConnectError = useCallback(
    (message: string) => {
      pushToast(plaidConnectErrorToast(message));
    },
    [pushToast],
  );

  const handleProfileEditSuccess = useCallback(
    (data: PatchProfileResponse) => {
      persistProfilePatchResponse(data);
      startTransition(() => {
        setSnapshot(buildDashboardSnapshot());
      });
      refreshLiveData();
      notifyNumbersChanged();
    },
    [refreshLiveData, notifyNumbersChanged],
  );

  const handleTargetEditSuccess = useCallback(() => {
    refreshLiveData();
    notifyNumbersChanged();
  }, [refreshLiveData, notifyNumbersChanged]);
  const targetEditOpts = useMemo<UseTargetEditOptions>(
    () => ({
      getToken: () => getToken(),
      getBaseTargetId: () => activeTargetId,
      getTargetDraft: () =>
        buildTargetEditDraft(
          activeReadiness.data?.target ?? null,
          displaySnapshot,
        ),
      onSuccess: handleTargetEditSuccess,
      onStaleRebase: refreshLiveData,
      onUncertain: refreshLiveData,
    }),
    [
      activeReadiness.data?.target,
      activeTargetId,
      displaySnapshot,
      getToken,
      handleTargetEditSuccess,
      refreshLiveData,
    ],
  );
  const profileEditOpts = useMemo<UseProfileEditOptions | undefined>(() => {
    if (!currentSnapshotId) return undefined;
    return {
      getToken: () => getToken(),
      getBaseSnapshotId: () => currentSnapshotId,
      onSuccess: handleProfileEditSuccess,
      onStaleRebase: refreshLiveData,
    };
  }, [getToken, currentSnapshotId, handleProfileEditSuccess, refreshLiveData]);

  const advisorInitialPrompt = searchParams.get("prompt") ?? undefined;
  const advisorInitialPromptAction =
    advisorInitialPrompt &&
    searchParams.get("new_chat") === "1" &&
    searchParams.get("send") === "1"
      ? "submit_new"
      : "draft";

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    if (!isSignedIn) {
      return;
    }

    let cancelled = false;

    void (async () => {
      await Promise.resolve();
      if (cancelled) return;
      setDashboardBootstrapSettled(false);
      setDashboardBootstrapError(null);

      try {
        const token = await getToken();
        if (!token) {
          return;
        }

        const [result, propertiesResult] = await Promise.all([
          fetchCurrentUser(token).catch(() => null),
          listSavedProperties({ limit: 3 }, token).catch(() => null),
        ]);
        const shouldPreviewTrackedProperties = activeView === "overview";
        const readinessPreviews =
          propertiesResult && shouldPreviewTrackedProperties
            ? await previewSavedPropertiesReadiness(
                propertiesResult.properties,
                token,
                activeReadinessTarget,
              )
            : null;

        // Extract platform tour state BEFORE isCompletedOnboardingResult narrowing (M3).
        // CurrentUserResponse carries app_state; OnboardingResponse does not.
        const bootstrapAppState =
          result != null && "app_state" in result ? result.app_state : null;

        if (isCompletedOnboardingResult(result)) {
          saveOnboardingResult(result);
        } else if (result) {
          clearOnboardingResult();
        }

        if (cancelled) {
          return;
        }

        setDashboardBootstrapError(null);
        // Commit the platform tour state decoded from app_state.
        setBootstrapTourState(
          extractTourStateFromBootstrap(bootstrapAppState ?? null),
        );
        // Durable finish-profile acknowledgement (Layer 2) from the same map.
        setFinishProfileAck(
          extractFinishProfileAckFromBootstrap(bootstrapAppState ?? null),
        );
        setReferralBannerAck(
          extractReferralBannerAckFromBootstrap(bootstrapAppState ?? null),
        );
        setReferralPublishPromptAck(
          extractReferralPublishPromptAckFromBootstrap(bootstrapAppState ?? null),
        );
        startTransition(() => {
          const nextSnapshot = buildDashboardSnapshot();
          if (propertiesResult) {
            nextSnapshot.trackedProperties = propertiesResult.properties.map(
              (property) =>
                mapSavedPropertyToTrackedProperty(
                  property,
                  readinessPreviews?.[property.id],
                ),
            );
          }
          setSnapshot(nextSnapshot);
        });
      } catch {
        // Keep the local preview snapshot when bootstrap calls fail.
        if (!cancelled) {
          setDashboardBootstrapError(new Error("Dashboard bootstrap failed"));
        }
      } finally {
        if (!cancelled) {
          setDashboardBootstrapSettled(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeReadinessTarget,
    activeView,
    dashboardRefreshNonce,
    getToken,
    isLoaded,
    isSignedIn,
  ]);

  // SILL-173: the legacy 5-milestone counter that was piped into the
  // 6-step JourneyRail is gone. Each rail step now reads its own
  // observable predicate from the snapshot inside `OverviewView`.
  const setDashboardState = (updates: {
    context?: AdvisorContext;
    newChat?: boolean;
    panel?: AdvisorPanel;
    prompt?: string | null;
    submitPrompt?: boolean;
    view?: DashboardView;
  }) => {
    setSearchParams(buildDashboardSearchParams(searchParams, updates), {
      replace: true,
    });
  };

  const openAdvisorWorkspace = (
    context: AdvisorContext = advisorContext,
    prompt: string | null = null,
    opts: { newChat?: boolean; submitPrompt?: boolean } = {},
  ) =>
    setDashboardState({
      context: context ?? "score",
      newChat: opts.newChat ?? false,
      panel: "chat",
      prompt,
      submitPrompt: opts.submitPrompt ?? false,
      view: "advisor",
    });

  // SILL-173: prompt-suggestions widget passes the chip's label here.
  // Open the advisor and seed the `prompt` searchParam so the chat
  // surface uses it as the initial prompt (already wired into
  // AdvisorView via `advisorInitialPrompt`).
  const handleAskSillPrompt = (prompt: string) => {
    const next = buildDashboardSearchParams(searchParams, {
      context: "score",
      panel: "chat",
      view: "advisor",
    });
    next.set("prompt", prompt);
    setSearchParams(next, { replace: true });
  };

  const navigatePrimaryView = (view: DashboardPrimaryView) => {
    setIsMobileNavOpen(false);
    setIsMobileScoreOpen(false);

    if (view === "advisor") {
      // Plan 435 M6 (asd-entry-repoint): the sidebar "Ask Sill" item AND
      // the mobile bottom-bar "Ask Sill" tab both route through this one
      // handler — both now open the drawer (a full-height sheet on
      // mobile) instead of navigating to `?view=advisor`.
      openAdvisorDrawer("sidebar");
      return;
    }

    if (view === "properties") {
      setPropertiesIntent(null);
    }
    setDashboardState({ view });
  };

  const navigateToFinancialDetails = (
    item: FinanceReadinessPreviewItem | null = null,
  ) => {
    void item;
    setDashboardState({ view: "passport" });
  };

  // SILL-304: deep-link into the Finances tab's #verify anchor where the
  // banner and per-row pills live. Used by the OverviewView's primary
  // CTA. Two-step (state change, then scroll) because the anchor needs
  // to be in the DOM before we can scroll to it. scrollIntoView is
  // gated because jsdom doesn't implement it — leaving it ungated
  // throws an unhandled error in test runs.
  const navigateToVerifyFinances = () => {
    // ?view=passport is canonical. Legacy ?view=profile and ?view=financials
    // remain aliases in resolveDashboardView.
    setDashboardState({ view: "passport" });
    window.requestAnimationFrame(() => {
      const target = document.getElementById("verify");
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };

  const navigateToDocuments = () => {
    setDashboardState({ view: "documents" });
  };

  const navigateToLinkedBanks = () => {
    navigate("/dashboard/settings#linked-banks");
  };

  const navigateToOnboardingStep = (
    category: FinanceReadinessPreviewCategory,
  ) => {
    navigate(`/onboarding?step=${onboardingStepForFinanceCategory(category)}`);
  };

  const openPropertiesView = (intent: PropertiesViewIntent = null) => {
    setPropertiesIntent(intent);
    setDashboardState({ view: "properties" });
  };

  const handleTrackedPropertiesChange = (
    trackedProperties: TrackedProperty[],
  ) => {
    startTransition(() => {
      setSnapshot((current) => ({ ...current, trackedProperties }));
    });
  };

  return (
    <div
      className={cn(
        "min-h-dvh bg-bg-warm lg:h-dvh lg:min-h-0 lg:overflow-hidden lg:pb-0",
        activeView === "advisor" ? "pb-0" : "pb-[calc(6rem+env(safe-area-inset-bottom))]",
      )}
    >
      <DashboardSidebarGrid
        sidebarWidth={computeSidebarWidth(activeView, isAdvisorRailExpanded)}
      >
        <DesktopSidebar
          activeView={activeView}
          marketplaceEnabled={marketplaceEnabled}
          feedEnabled={feedEnabled}
          isRailExpanded={
            usesAbbreviatedSidebar ? isAdvisorRailExpanded : undefined
          }
          offersCount={offersCount}
          onNavigate={navigatePrimaryView}
          onNavigatePath={(path) => navigate(path)}
          onOpenSettings={() => navigate("/dashboard/settings")}
          onToggleRailExpanded={
            usesAbbreviatedSidebar
              ? () => setIsAdvisorRailExpanded((current) => !current)
              : undefined
          }
          readiness={activeReadiness.data?.result ?? null}
          readinessTarget={activeReadiness.data?.target ?? null}
          readinessRefreshing={isReadinessRefreshing}
          snapshot={displaySnapshot}
          variant={usesAbbreviatedSidebar ? "rail" : "default"}
          marketplaceConnectionStatus={marketplaceConnection?.status ?? null}
          hasPendingMarketplaceRequest={
            pendingRequest?.status === "pending_pro"
          }
          isQueuedForMarketplace={waitlistPosition != null}
        />

        <div
          className={cn(
            "min-w-0 px-4 sm:px-5 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:overscroll-none lg:px-6",
            activeView === "advisor"
              ? // The advisor surface manages its own internal scroll and must
                // fill the viewport exactly. At xl the content frame becomes a
                // flex column (header + flex-1 main) so the surface fills the
                // leftover height precisely — no 100vh math that drifts past the
                // dvh-sized shell and leaves a permanent scrollbar.
                "pb-0 pt-5 lg:pb-0 lg:pt-6 xl:flex xl:flex-col xl:overflow-hidden"
              : "py-5 lg:py-6",
          )}
          data-slot="dashboard-content-frame"
        >
          <DashboardHeader
            activeView={activeView}
            feedUnreadCount={feedUnreadCount}
            isMobileNavOpen={isMobileNavOpen}
            isMobileScoreOpen={isMobileScoreOpen}
            onNavigateOverview={() => navigatePrimaryView("overview")}
            onOpenAdvisor={() => {
              setIsMobileNavOpen(false);
              setIsMobileScoreOpen(false);
              // Plan 435 M6 (asd-entry-repoint): the header "Ask Sill" pill
              // opens the drawer instead of navigating to `?view=advisor`.
              openAdvisorDrawer("header");
            }}
            readiness={activeReadiness.data?.result ?? null}
            readinessRefreshing={isReadinessRefreshing}
            onToggleMobileNav={() => setIsMobileNavOpen((current) => !current)}
            onToggleMobileScore={() =>
              setIsMobileScoreOpen((current) => !current)
            }
          />

          <main
            className={cn(
              "min-w-0",
              activeView === "advisor"
                ? "-mx-4 sm:-mx-5 lg:-mx-6 xl:min-h-0 xl:flex-1"
                : "content-shell",
              activeView === "overview"
                ? "pt-6"
                : activeView === "advisor"
                  ? "pt-0"
                  : "pt-4",
            )}
          >
            {/* Referral discovery banner (Overview + Passport). Waits for the
                bootstrap-decoded dismissal so it never flashes-then-hides. */}
            {(activeView === "overview" ||
              (activeView === "passport" && !publishPromptActive)) &&
            referralBannerAck !== undefined ? (
              <ReferralBannerContainer
                bootstrapAck={referralBannerAck}
                waitlistArea={waitlistPosition?.area_label ?? null}
              />
            ) : null}

            {activeView === "overview" ? (
              <AnimatePresence initial={false} mode="wait">
                {overviewReady ? (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    initial={{ opacity: 0, y: 8 }}
                    key="overview-ready"
                    transition={{ duration: 0.24, ease: [0.2, 0, 0, 1] }}
                  >
                    <HomeView
                      getToken={getToken}
                      marketplaceConnection={{
                        connection: marketplaceConnection,
                        pendingRequest,
                        refresh: refreshMarketplaceConnection,
                        settled: marketplaceConnectionSettled,
                      }}
                      waitlistPosition={waitlistPosition}
                      refreshWaitlist={refreshWaitlist}
                      bootstrapTourState={bootstrapTourState}
                      journeyRailDismissed={journeyRailDismissed}
                      onAddProperty={() =>
                        openPropertiesView({ kind: "create" })
                      }
                      onAskSillPrompt={handleAskSillPrompt}
                      onCompleteOnboarding={() => navigate("/onboarding")}
                      onDismissJourneyRail={handleDismissJourneyRail}
                      onRestoreJourneyRail={handleRestoreJourneyRail}
                      onJourneyStepAction={(
                        step: DashboardJourneyStep,
                        actionKey,
                      ) => {
                        posthog?.capture?.("overview_journey_step_action", {
                          actionKey,
                          stepId: step.id,
                        });

                        if (step.id === "enter-data") {
                          navigate("/onboarding");
                          return;
                        }
                        if (step.id === "ask-sill") {
                          // Plan 435 M6 (asd-entry-repoint): Home hero /
                          // journey-rail "Ask Sill" entry opens the drawer
                          // with the home context.
                          openAdvisorDrawer("cta", { kind: "home" });
                          return;
                        }
                        if (step.id === "verify-finances") {
                          navigateToDocuments();
                          return;
                        }
                        if (step.id === "dpa") {
                          navigate("/calculators/dpa-programs");
                          return;
                        }
                        if (step.id === "add-property") {
                          openPropertiesView({ kind: "create" });
                          return;
                        }
                        if (step.id === "publish-profile") {
                          setDashboardState({ view: "passport" });
                          return;
                        }
                        notifyStub(
                          "Coming soon",
                          "This step will be wired up shortly.",
                        );
                      }}
                      onOpenFinancials={navigateToFinancialDetails}
                      onOpenProperties={() => openPropertiesView()}
                      onOpenProperty={(propertyID) =>
                        navigate(`/properties/${propertyID}`)
                      }
                      onOpenRateWatch={() =>
                        setDashboardState({ view: "rate-watch" })
                      }
                      onScoreFocus={() =>
                        openAdvisorDrawer("cta", { kind: "home" })
                      }
                      onSelectVerificationSource={(source, item) => {
                        if (source === "manual") {
                          if (item) {
                            navigateToOnboardingStep(item.category);
                          } else {
                            navigate("/onboarding");
                          }
                          return;
                        }
                        if (source === "doc") {
                          navigateToDocuments();
                          return;
                        }
                        navigateToFinancialDetails(item ?? null);
                      }}
                      onVerificationUpgrade={navigateToVerifyFinances}
                      readinessLoading={
                        activeReadiness.isLoading && !activeReadiness.data
                      }
                      readiness={activeReadiness.data?.result ?? null}
                      readinessTarget={activeReadiness.data?.target ?? null}
                      snapshot={displaySnapshot}
                    />
                  </motion.div>
                ) : (
                  <OverviewSkeleton
                    journeyRailDismissed={journeyRailDismissed}
                    key="overview-skeleton"
                  />
                )}
              </AnimatePresence>
            ) : null}

            {activeView === "passport" ? (
              <ProfileView
                waitlistSlot={
                  marketplaceEnabled ? (
                    <WaitlistPositionCardContainer />
                  ) : undefined
                }
                referralPromptSlot={
                  referralPublishPromptAck !== undefined ? (
                    <ReferralPublishPromptContainer
                      ack={referralPublishPromptAck}
                      justPublished={justPublished}
                      published={displaySnapshot.portalDiscoverable ?? false}
                      waitlistArea={waitlistPosition?.area_label ?? null}
                      onShown={() => setPublishPromptActive(true)}
                      onDismissed={() => setPublishPromptActive(false)}
                    />
                  ) : undefined
                }
                onPublished={() => {
                  setJustPublished(true);
                  // Publish is what queues a pre-launch buyer (Reconcile), so
                  // re-read the position: the prompt's waitlist copy and the
                  // block's "Waitlisted for {area}" relabel both depend on it.
                  void refreshWaitlist();
                }}
                waitlistArea={waitlistPosition?.area_label ?? null}
                isConnected={isConnectedToAgent}
                connectedProName={
                  marketplaceConnection?.status === "active"
                    ? marketplaceConnection.pro_name
                    : undefined
                }
                onNavigateToYourTeam={() => navigate("/team")}
                onAddProperty={() => openPropertiesView({ kind: "create" })}
                onOpenAdvisor={(prompt = ADVISOR_PROMPTS.profileReview) =>
                  openAdvisorWorkspace("rates", prompt, {
                    newChat: true,
                    submitPrompt: true,
                  })
                }
                readiness={activeReadiness.data?.result ?? null}
                snapshot={displaySnapshot}
                target={activeReadiness.data?.target ?? null}
                locationHash={location.hash}
                targetEditOpts={targetEditOpts}
                profileEditOpts={profileEditOpts}
                getToken={getToken}
                onPlaidConnected={() => {
                  // SILL-304: after Plaid Link succeeds, both the
                  // snapshot (financial inputs / hasPlaidItem) and the
                  // readiness score may have changed — refreshLiveData
                  // wraps both.
                  void refreshLiveData();
                }}
                onPlaidConnectError={notifyPlaidConnectError}
                // SILL-302: manual-entry mutations need the same
                // post-success refresh as useProfileEdit — readiness
                // refetch + bootstrap re-fetch (which rebuilds the
                // dashboard snapshot and refreshes the aggregate row
                // summary). refreshLiveData wraps both.
                onManualEntryMutation={refreshLiveData}
                // 2026-05-28: bump the manual-entries reload in lockstep
                // with readiness + bootstrap so Plaid-sourced contributor
                // rows (created server-side during exchange/sync) appear
                // immediately after onPlaidConnected fires. Without this,
                // the row header sum updates (via /api/users/me) but the
                // expanded entries list stays stale until a hard refresh.
                manualEntriesRefreshKey={dashboardRefreshNonce}
                onNavigateToDocuments={navigateToDocuments}
                onNavigateToLinkedBanks={navigateToLinkedBanks}
              />
            ) : null}

            {activeView === "properties" ? (
              <PropertiesView
                getToken={getToken}
                intent={propertiesIntent}
                isLoaded={isLoaded}
                isSignedIn={!!isSignedIn}
                onIntentHandled={() => setPropertiesIntent(null)}
                onOpenProperty={(propertyID) =>
                  navigate(`/properties/${propertyID}`)
                }
                onReadinessChanged={() => {
                  void refreshReadiness();
                }}
                onTrackedPropertiesChange={handleTrackedPropertiesChange}
                snapshot={displaySnapshot}
              />
            ) : null}

            {activeView === "documents" ? (
              <DocumentsView
                getToken={getToken}
                isLoaded={isLoaded}
                isSignedIn={!!isSignedIn}
                onReadinessChanged={() => {
                  void refreshReadiness();
                }}
              />
            ) : null}

            {activeView === "readiness" ? (
              <ReadinessPageView
                isLoading={activeReadiness.isLoading}
                result={activeReadiness.data?.result ?? null}
                snapshot={displaySnapshot}
                target={activeReadiness.data?.target ?? null}
              />
            ) : null}

            {activeView === "rate-watch" ? (
              <RateWatchView
                inAppNotificationsEnabled={true}
                // Plan 435 M6 (asd-entry-repoint): Rate Watch's "Ask Sill"
                // CTAs open the drawer instead of navigating away.
                onOpenAdvisor={() => openAdvisorDrawer("cta")}
                onOpenFinancials={() => setDashboardState({ view: "passport" })}
                onOpenSettings={() =>
                  navigate("/dashboard/settings#notifications")
                }
                snapshot={displaySnapshot}
              />
            ) : null}

            {/* activeView === "feed" never renders here (Plan 287 M3):
                /feed is a dedicated route at FeedPage. Legacy ?view=feed
                deep-links resolve to "overview" via the activeView derivation
                above. The FeedView stub in dashboard-views.tsx is retired. */}

            {activeView === "advisor" ? (
              <AdvisorView
                activePanel={advisorPanel}
                initialPromptAction={advisorInitialPromptAction}
                initialPrompt={advisorInitialPrompt}
                onPanelChange={(panel) =>
                  setDashboardState({ panel, view: "advisor" })
                }
                onOpenSettings={() => navigate("/dashboard/settings")}
                onProfileSnapshotUpdated={refreshLiveData}
                snapshot={displaySnapshot}
              />
            ) : null}
          </main>
        </div>
      </DashboardSidebarGrid>

      <MobileTabBar
        activeView={activeView}
        marketplaceEnabled={marketplaceEnabled}
        onNavigate={navigatePrimaryView}
        onNavigatePath={(path) => navigate(path)}
        onOpenSettings={() => navigate("/dashboard/settings")}
      />
      <MobileSidebar
        activeView={activeView}
        marketplaceEnabled={marketplaceEnabled}
        feedEnabled={feedEnabled}
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        onNavigate={navigatePrimaryView}
        onNavigatePath={(path) => {
          setIsMobileNavOpen(false);
          setIsMobileScoreOpen(false);
          navigate(path);
        }}
        onOpenSettings={() => {
          setIsMobileNavOpen(false);
          setIsMobileScoreOpen(false);
          navigate("/dashboard/settings");
        }}
      />

      <FinishProfileModal
        open={showFinishProfile}
        fields={finishProfileFields}
        onComplete={() => {
          acknowledgeFinishProfile("completed");
          navigatePrimaryView("passport");
        }}
        onDismiss={() => {
          acknowledgeFinishProfile("dismissed");
        }}
      />
    </div>
  );
}

const READINESS_FACTOR_LABELS: Record<ReadinessFactorID, string> = {
  buying_power: "Buying power",
  down_payment: "Cash",
  cash_execution: "Plan funding",
  credit: "Credit",
  dti: "DTI",
  timeline: "Timing",
  stability: "Stability",
  work_history: "Work history",
  profile_completeness: "Profile",
};

type MobileReadinessFactor = {
  confidence: string;
  id: ReadinessFactorID;
  label: string;
  ratio: number;
  tone: "attention" | "steady" | "strong";
};

function buildMobileReadinessFactors(
  readiness: ReadinessResult | null | undefined,
): MobileReadinessFactor[] {
  if (!readiness) {
    return [];
  }

  return (
    Object.entries(readiness.factors) as [
      ReadinessFactorID,
      { value: number; max: number; applies: boolean },
    ][]
  )
    .filter(([, factor]) => factor.applies)
    .map(([id, factor]): MobileReadinessFactor => {
      const ratio = factor.max > 0 ? factor.value / factor.max : 0;
      const confidence = readiness.confidence_map[id];
      const tone: MobileReadinessFactor["tone"] =
        ratio >= 0.75 ? "strong" : ratio >= 0.45 ? "steady" : "attention";
      return {
        confidence:
          typeof confidence === "number"
            ? `${Math.round(confidence * 100)}% confidence`
            : "Self-reported",
        id,
        label: READINESS_FACTOR_LABELS[id],
        ratio,
        tone,
      };
    })
    .sort((a, b) => a.ratio - b.ratio)
    .slice(0, 3);
}

function dollarsForTarget(target: ReadinessTarget, fallback: number) {
  const rawPrice = target.deal.property.target_price;
  if (typeof rawPrice !== "number" || rawPrice <= 0) {
    return fallback;
  }
  return Math.round(rawPrice / 100);
}

function buildTargetEditDraft(
  target: ReadinessTarget | null,
  snapshot: DashboardSnapshot,
): TargetEditPatch {
  const property = target?.deal.property;
  const displayArea = target
    ? displayReadinessTargetArea(target, snapshot.targetArea)
    : snapshot.targetArea;
  const price = target
    ? dollarsForTarget(target, snapshot.targetPrice)
    : snapshot.targetPrice;
  const locationCode =
    property?.location_code || displayArea || target?.label || "";

  const draft: TargetEditPatch = {
    kind: target?.kind ?? "generic",
    label:
      normalizeReadinessTargetLabel(target?.label) ||
      displayArea ||
      locationCode ||
      "Readiness target",
    location_code: locationCode,
    price: price > 0 ? price : 0,
    estimated_utilities: target?.estimated_utilities ?? null,
    estimated_maintenance: target?.estimated_maintenance ?? null,
    timeline:
      normalizeReadinessTimeline(target?.timeline ?? snapshot.timelineToken) ??
      "3_6_months",
  };

  if (property?.occupancy) {
    draft.occupancy = property.occupancy;
  }
  if (typeof property?.units === "number" && property.units > 0) {
    draft.units = property.units;
  }
  if (property?.property_type) {
    draft.property_type = property.property_type;
  }
  if (target?.deal.preferred_product) {
    draft.preferred_product = target.deal.preferred_product;
  }
  if (target?.deal.preferred_term_mo) {
    draft.preferred_term_mo = target.deal.preferred_term_mo;
  }
  if (target?.property_id) {
    draft.property_id = target.property_id;
  }
  if (target?.deal.buyer_type) {
    draft.buyer_type = target.deal.buyer_type;
  }
  if (target?.deal.savings_goal_pct) {
    draft.savings_goal_pct = target.deal.savings_goal_pct;
  }
  if (target?.deal.savings_goal_amount) {
    draft.savings_goal_amount = target.deal.savings_goal_amount;
  }

  return draft;
}

function normalizeReadinessTimeline(
  value: ReadinessTimeline | string | null | undefined,
): ReadinessTimeline | null {
  switch (value) {
    case "asap":
    case "1-3months":
    case "0_3_months":
      return "0_3_months";
    case "3-6months":
    case "3_6_months":
      return "3_6_months";
    case "6-12months":
    case "6_12_months":
      return "6_12_months";
    case "exploring":
    case "12_plus":
      return "12_plus";
    default:
      return null;
  }
}

type MobilePrimaryNavItem =
  | {
      icon: DashboardNavIcon;
      label: string;
      view: DashboardPrimaryView;
    }
  | {
      icon: DashboardNavIcon;
      label: string;
      href: string;
    }
  | {
      icon: DashboardNavIcon;
      label: string;
      settings: true;
    };

// task 2034 (mfh-mobile-tabbar, operator decision 2026-10-08): Home · Browse
// agents · Passport · Ask Sill — Account is dropped from the tab bar (it
// stays reachable from the mobile hamburger menu's own "Account" row,
// unconditionally rendered by `MobileSidebar` below). Browse agents is the
// one `href` item; it's gated at render time by the SAME `marketplaceEnabled`
// `filterDashboardNavGroups` already gates the desktop sidebar's Browse
// agents item with — no bespoke gated-off design for the tab bar.
// UI-verify fix (M3 iteration 3): "Browse agents" wraps to two lines at
// 320px in the tab bar's narrow column. Operator wording — the TAB says
// "Browse"; the desktop sidebar item (`SIDEBAR_NAV_GROUPS`, a separate
// array) keeps "Browse agents" unchanged.
const MOBILE_PRIMARY_NAV_ITEMS: MobilePrimaryNavItem[] = [
  { icon: House, label: "Home", view: "overview" },
  { icon: Search, label: "Browse", href: marketplaceBrowseHref("agent") },
  {
    icon: IdCard,
    label: "Passport",
    view: "passport",
  },
  {
    icon: MessageSquareText,
    label: "Ask Sill",
    view: "advisor",
  },
];

function isDashboardPathActive(
  activeView: ShellActiveView | undefined,
  currentLocation: string,
  href: string,
): boolean {
  if (href.startsWith("/marketplace/browse")) {
    return activeView === "marketplace-browse";
  }
  if (href === "/offers") {
    return activeView === "offers";
  }
  if (href === "/threads") {
    return activeView === "messages";
  }
  const [currentBeforeHash, currentHash = ""] = currentLocation.split("#", 2);
  const [hrefBeforeHash, hrefHash = ""] = href.split("#", 2);
  const currentPath = currentBeforeHash.split("?")[0];
  const hrefPath = hrefBeforeHash.split("?")[0];
  const pathMatches =
    currentPath === hrefPath || currentPath.startsWith(`${hrefPath}/`);

  return pathMatches && (!hrefHash || currentHash === hrefHash);
}

function getMobileSidebarItems(
  feedEnabled: boolean,
  marketplaceEnabled: boolean,
  messagingEnabled: boolean,
  messagesUnreadCount: number | null,
  offersCount: number | null,
): DashboardNavItem[] {
  return filterDashboardNavGroups({
    feedEnabled,
    marketplaceEnabled,
    messagingEnabled,
    messagesUnreadCount,
    offersCount,
  }).flatMap((group) =>
    group.items.filter(
      (
        item,
      ): item is Extract<
        DashboardNavItem,
        | { status: "live"; view: DashboardPrimaryView }
        | { status: "live"; href: string }
        | { status: "live"; action: "feedback" }
      > => item.status === "live",
    ),
  );
}

function getUserInitials(firstName: string) {
  return firstName.slice(0, 2).toUpperCase();
}

function compactSidebarAddress(address: string): string {
  return address
    .replace(/\bAvenue\b/g, "Ave")
    .replace(/\bBoulevard\b/g, "Blvd")
    .replace(/\bCircle\b/g, "Cir")
    .replace(/\bCourt\b/g, "Ct")
    .replace(/\bDrive\b/g, "Dr")
    .replace(/\bHighway\b/g, "Hwy")
    .replace(/\bLane\b/g, "Ln")
    .replace(/\bParkway\b/g, "Pkwy")
    .replace(/\bPlace\b/g, "Pl")
    .replace(/\bRoad\b/g, "Rd")
    .replace(/\bStreet\b/g, "St")
    .replace(/\bTerrace\b/g, "Ter")
    .replace(/\bTrail\b/g, "Trl")
    .replace(/\bNorthwest\b/g, "NW")
    .replace(/\bNortheast\b/g, "NE")
    .replace(/\bSouthwest\b/g, "SW")
    .replace(/\bSoutheast\b/g, "SE")
    .replace(/\bNorth\b/g, "N")
    .replace(/\bSouth\b/g, "S")
    .replace(/\bEast\b/g, "E")
    .replace(/\bWest\b/g, "W");
}

type SidebarTargetDisplay =
  | {
      addressLine: string;
      ctaHref: string;
      ctaLabel: string;
      kind: "property";
      price: string;
    }
  | {
      copy: string;
      ctaHref: string;
      ctaLabel: string;
      kind: "plain";
    };

function sidebarTargetDisplay(
  target: ReadinessTarget | null | undefined,
  snapshot: DashboardSnapshot,
): SidebarTargetDisplay {
  if (!target) {
    return {
      copy: "Finish onboarding to unlock readiness.",
      ctaHref: "/onboarding",
      ctaLabel: "Finish onboarding",
      kind: "plain",
    };
  }
  if (target?.kind === "property") {
    const trackedProperty = target.property_id
      ? snapshot.trackedProperties.find(
          (property) => property.id === target.property_id,
        )
      : null;
    const rawTargetPrice = target.deal.property.target_price;
    const targetPrice =
      trackedProperty?.listPrice ??
      (typeof rawTargetPrice === "number" && rawTargetPrice > 0
        ? Math.round(rawTargetPrice / 100)
        : snapshot.targetPrice);
    const targetAddressLine = target.label.split(",")[0]?.trim();
    const addressLine =
      (targetAddressLine ? compactSidebarAddress(targetAddressLine) : "") ||
      (trackedProperty?.address
        ? compactSidebarAddress(trackedProperty.address)
        : "this property");
    return {
      addressLine,
      ctaHref: target.property_id
        ? `/properties/${target.property_id}`
        : "/dashboard?view=passport",
      ctaLabel: target.property_id ? "View property" : "View target",
      kind: "property",
      price: formatCompactCurrency(targetPrice),
    };
  }

  const price = formatCompactCurrency(
    dollarsForTarget(target, snapshot.targetPrice),
  );
  const area =
    displayReadinessTargetArea(target, snapshot.targetArea) ||
    "your target area";
  return {
    copy: `Tracking ${price} in ${area}.`,
    ctaHref: "/dashboard?view=passport",
    ctaLabel: "View target",
    kind: "plain",
  };
}

export type DashboardHeaderBreadcrumbItem = {
  label: string;
  onClick?: () => void;
};

export function DesktopSidebar({
  activeView,
  feedEnabled = false,
  marketplaceEnabled = false,
  isRailExpanded = false,
  offersCount,
  onNavigate,
  onNavigatePath,
  onOpenSettings,
  onToggleRailExpanded,
  readiness,
  readinessTarget,
  readinessRefreshing = false,
  snapshot,
  suppressClerkControls = false,
  variant = "default",
  marketplaceConnectionStatus = null,
  hasPendingMarketplaceRequest = false,
  isQueuedForMarketplace = false,
  competingAgentCount = null,
}: {
  /** ShellActiveView, or undefined when the current route matches no known
   * sidebar destination (plan 291 simplify pass 2 — `marketplaceShellDestination`
   * highlights nothing rather than defaulting to "offers"). */
  activeView: ShellActiveView | undefined;
  feedEnabled?: boolean;
  marketplaceEnabled?: boolean;
  isRailExpanded?: boolean;
  /** Your Offers badge count. null/undefined = badge hidden. 0 = badge shows "0". */
  offersCount?: number | null;
  onNavigate: (view: DashboardPrimaryView) => void;
  onNavigatePath: (path: string) => void;
  onOpenSettings: () => void;
  onToggleRailExpanded?: () => void;
  readiness?: ReadinessResult | null;
  readinessTarget?: ReadinessTarget | null;
  readinessRefreshing?: boolean;
  snapshot: DashboardSnapshot;
  /** DEV preview seam: retain real nav chrome without mounting Clerk-only controls. */
  suppressClerkControls?: boolean;
  variant?: "default" | "rail";
  /** Plan 435 M1 (mfh-sidebar-passport-card). Already-fetched inputs — no
   *  new marketplace read happens here. */
  marketplaceConnectionStatus?: string | null;
  hasPendingMarketplaceRequest?: boolean;
  isQueuedForMarketplace?: boolean;
  /** "N agents competing" — omitted (no line) until a source exists (M3). */
  competingAgentCount?: number | null;
}) {
  const currentLocation =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : "";
  const targetDisplay = sidebarTargetDisplay(readinessTarget, snapshot);
  const score = readiness?.score;
  // SILL-323 follow-up: `score` is null when a cap override fires. Use
  // the same "—" / "Not yet qualified" pre-scale framing the score
  // panel already uses, so the sidebar doesn't contradict the cap
  // banner with a numeric headline.
  const scoreIsCapped = readiness != null && readiness.score === null;
  const scoreAppearance =
    typeof score === "number" ? getReadinessScoreAppearance(score) : null;
  const scoreFillClassName = scoreAppearance?.markerClassName ?? "bg-border";
  const targetLinkColorClassName =
    scoreAppearance?.textClassName ?? "text-primary";
  const highestTier = deriveHighestVerificationTier(snapshot);
  // Plan 435 M1 (mfh-sidebar-passport-card). Every input here is already
  // fetched elsewhere in DashboardPage (marketplaceConnection, the folded
  // pendingRequest, useWaitlist, snapshot.portalDiscoverable) — this derives
  // the dot/label, it never issues a read.
  const passportLiveState = deriveSidebarPassportLiveState({
    portalDiscoverable: snapshot.portalDiscoverable ?? false,
    isQueued: isQueuedForMarketplace,
    connectionStatus: marketplaceConnectionStatus,
    hasPendingRequest: hasPendingMarketplaceRequest,
  });
  const passportLiveStateCopy =
    SIDEBAR_PASSPORT_LIVE_STATE_COPY[passportLiveState];
  const competingCopy = sidebarPassportCompetingCopy(competingAgentCount);
  const openTierExplainer = useVerifiedStore((s) => s.openExplainer);
  const scoreLabel = scoreIsCapped
    ? "Not yet qualified"
    : (readiness?.label ?? (readinessRefreshing ? "Loading" : "No score yet"));
  // Messaging-inbox IA WS6: the Messages item + badge read their own hooks
  // here (rather than threading props through DashboardShellFrame AND
  // DashboardPage) — mirrors how the header derives messagingEnabled.
  const messagingEnabled = useMessagingEnabled();
  const messagesUnreadCount = useMessagesStore((s) => s.unreadCount);
  const storedOffersCount = useOffersStore((s) => s.count);
  const resolvedOffersCount =
    offersCount === undefined ? storedOffersCount : offersCount;
  const navGroups = filterDashboardNavGroups({
    feedEnabled,
    marketplaceEnabled,
    // Pre-launch invited-buyer posture: suppress the "Browse agents · Soon"
    // tease for buyers whose agent invited them (see the helper's doc).
    hasConnectedAgent: snapshot.agentConnected,
    messagingEnabled,
    messagesUnreadCount,
    offersCount: resolvedOffersCount,
  });
  const isRail = variant === "rail";
  const isCompactRail = isRail && !isRailExpanded;
  const support = useSupport();
  return (
    <aside
      aria-label="Desktop sidebar"
      className={cn(
        "hidden overflow-x-hidden border-r border-border bg-sidebar lg:flex lg:h-full lg:min-w-0 lg:w-full lg:flex-col motion-safe:transition-[background-color,box-shadow] motion-safe:duration-[420ms] motion-safe:ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
        isCompactRail && "bg-sidebar/75",
        isRailExpanded && "shadow-lg",
      )}
    >
      <div
        className={cn(
          "flex h-12 items-center",
          isCompactRail ? "justify-center px-3" : "justify-between px-4",
        )}
      >
        {isCompactRail ? (
          <SidebarStepperButton
            active={isRailExpanded}
            compact
            icon={Menu}
            iconOnly
            label="Expand sidebar"
            onClick={onToggleRailExpanded}
            testId="sidebar-rail-toggle"
          />
        ) : (
          <>
            <button
              type="button"
              onClick={() => onNavigate("overview")}
              aria-label="Go to Overview"
              className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <img src={sillLogo} alt="Sill" className="h-7" />
            </button>
            {isRail ? (
              <button
                aria-label="Collapse sidebar"
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition-[background-color,color,transform] hover:bg-muted hover:text-foreground active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="sidebar-rail-toggle"
                onClick={onToggleRailExpanded}
                title="Collapse sidebar"
                type="button"
              >
                <Menu className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
        {isCompactRail ? null : (
          <div className="px-4 py-2.5">
            <div
              className="rounded-[1.25rem] border border-border bg-card px-3 py-4 text-center shadow-sm"
              data-tier={highestTier}
            >
              {/* 2026-10-08: the score + label open the Readiness page —
                  the sidebar's score card is the natural door to it. */}
              <Link
                aria-label="See your readiness"
                className="group block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="sidebar-readiness-link"
                to="/dashboard?view=readiness"
              >
              <div
                aria-busy={readinessRefreshing}
                className="relative mx-auto inline-flex min-h-12 min-w-20 items-center justify-center"
              >
                <p
                  data-testid="sidebar-readiness-score"
                  className={cn(
                    "text-4xl font-semibold tracking-[-0.04em] text-foreground transition-opacity duration-200",
                    readinessRefreshing && "opacity-20",
                  )}
                >
                  {scoreIsCapped
                    ? "—"
                    : typeof score === "number"
                      ? score
                      : "--"}
                </p>
                {readinessRefreshing ? (
                  <span
                    aria-label="Updating readiness score"
                    className="absolute inset-0 grid place-items-center rounded-2xl bg-card/70 text-primary backdrop-blur-[1px]"
                    data-testid="sidebar-readiness-loading"
                    role="status"
                  >
                    <Spinner className="h-5 w-5" />
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-xs font-semibold text-foreground group-hover:underline group-hover:underline-offset-2">
                {scoreLabel}
              </p>
              </Link>
              <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
                {targetDisplay.kind === "property" ? (
                  <>
                    Tracking {targetDisplay.price} on{" "}
                    {targetDisplay.addressLine}.
                  </>
                ) : (
                  targetDisplay.copy
                )}
              </p>
              <Link
                className={cn(
                  "mt-2 inline-flex items-center justify-center gap-1 text-xs font-semibold underline underline-offset-2 hover:opacity-80",
                  targetLinkColorClassName,
                )}
                to={targetDisplay.ctaHref}
              >
                {targetDisplay.ctaLabel}
                <ArrowRight className="h-3 w-3" />
              </Link>
              <div className="mt-3 h-1.5 rounded-full bg-border">
                <div
                  className={cn("h-1.5 rounded-full", scoreFillClassName)}
                  style={{ width: `${typeof score === "number" ? score : 0}%` }}
                />
              </div>
              <div className="mt-3 flex justify-center">
                <TierBadge
                  tier={highestTier}
                  size="sm"
                  onExplainRequest={openTierExplainer}
                />
              </div>
              {/* Plan 435 M1 (mfh-sidebar-passport-card): marketplace
                  live/queued/not-live/request-pending/connected status, from
                  already-fetched inputs (no new read). Hidden entirely while
                  the marketplace launch gate is closed — "Live to agents" /
                  "Waitlisted" would be a promise pre-launch production can't
                  keep yet. */}
              {marketplaceEnabled ? (
                <Link
                  className="mt-3 flex items-center justify-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
                  data-testid="sidebar-passport-live-status"
                  to="/dashboard?view=passport"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "h-1.5 w-1.5 rounded-full",
                      passportLiveStateCopy.dotClassName,
                    )}
                  />
                  <span>{passportLiveStateCopy.label}</span>
                  {competingCopy ? <span>{`· ${competingCopy}`}</span> : null}
                </Link>
              ) : null}
            </div>
          </div>
        )}

        <Divider
          className={cn(
            isCompactRail ? "mx-0 mb-[11px] mt-[7px]" : "mx-4 my-2.5",
          )}
        />

        <nav
          className={cn(
            "space-y-2 overflow-x-hidden px-3 pb-4",
            isCompactRail && "space-y-3",
          )}
        >
          {navGroups.map((group, groupIndex) => (
            <div key={`${groupIndex}-${group.label}`} className="space-y-px">
              {/* Plan 435 M1 (mfh-nav-regroup): a standalone item (e.g. Ask
                  Sill) carries an empty group label — no visible heading
                  row, matching "a standalone item" rather than a named
                  section. */}
              {group.label ? (
                <p
                  className={cn(
                    "px-2 pb-0.5 text-[8px] font-medium uppercase tracking-[0.18em] text-muted-foreground",
                    isCompactRail && "sr-only",
                  )}
                >
                  {group.label}
                </p>
              ) : null}

              {group.items.map((item) => {
                const isLiveItem = item.status === "live";
                const isPathItem = isLiveItem && "href" in item;
                const isViewItem = isLiveItem && "view" in item;
                const isActionItem = isLiveItem && "action" in item;
                const isActive = isViewItem
                  ? activeView === item.view
                  : isPathItem
                    ? isDashboardPathActive(
                        activeView,
                        currentLocation,
                        item.href,
                      )
                    : false;
                const badgeCount =
                  isPathItem &&
                  "badge" in item &&
                  typeof item.badge === "number"
                    ? item.badge
                    : undefined;
                const hoverCopy =
                  !isLiveItem && "hoverCopy" in item
                    ? item.hoverCopy
                    : undefined;

                return (
                  <div
                    key={`${group.label}-${item.label}`}
                    className="space-y-0.5"
                  >
                    <SidebarStepperButton
                      active={isActive}
                      badge={badgeCount}
                      compact={isCompactRail}
                      disabled={!isLiveItem}
                      icon={item.icon}
                      iconOnly={isCompactRail}
                      label={item.label}
                      soon={item.status === "planned"}
                      title={hoverCopy}
                      trailingIcon={item.trailingIcon}
                      onClick={
                        isViewItem
                          ? () => onNavigate(item.view)
                          : isPathItem
                            ? () => onNavigatePath(item.href)
                            : isActionItem && item.action === "feedback"
                              ? () => support.openFeedback()
                              : undefined
                      }
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
      </div>

      <div
        className={cn(
          "border-t border-border",
          isCompactRail ? "px-3 py-3" : "px-4 py-4",
        )}
      >
        <div
          className={cn(
            "flex items-center",
            isCompactRail ? "flex-col justify-center gap-2" : "justify-between",
          )}
        >
          <button
            type="button"
            onClick={onOpenSettings}
            className={cn(
              "inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted/80 hover:text-foreground hover:shadow-none",
              activeView === "settings" && "text-primary",
            )}
            aria-label="Open settings"
          >
            <Settings className="h-4 w-4" />
          </button>
          {suppressClerkControls ? null : (
            <SidebarPlusBadgeButton compact={isCompactRail} />
          )}
          <AvatarBadge
            firstName={snapshot.firstName}
            onClick={onOpenSettings}
          />
        </div>
      </div>
    </aside>
  );
}

export function MobileTabBar({
  activeView,
  marketplaceEnabled = false,
  onNavigate,
  onNavigatePath,
  onOpenSettings,
}: {
  activeView: ShellActiveView | undefined;
  /** task 2034: gates the "Browse agents" href item — same source of
   *  truth as the desktop sidebar's Browse agents item
   *  (`filterDashboardNavGroups`). */
  marketplaceEnabled?: boolean;
  onNavigate: (view: DashboardPrimaryView) => void;
  onNavigatePath: (path: string) => void;
  onOpenSettings: () => void;
}) {
  const currentLocation =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : "";
  const items = MOBILE_PRIMARY_NAV_ITEMS.filter(
    (item) => marketplaceEnabled || !("href" in item),
  );

  return (
    <nav
      aria-label="Mobile tab bar"
      className="fixed inset-x-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-20 rounded-[1.75rem] border border-border bg-card p-2 shadow-lg lg:hidden"
    >
      <div
        className="grid gap-2"
        style={{
          gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
        }}
      >
        {items.map((item) => {
          const Icon = item.icon;
          const isSettings = "settings" in item;
          const isPathItem = "href" in item;
          const isActive = isSettings
            ? activeView === "settings"
            : isPathItem
              ? isDashboardPathActive(activeView, currentLocation, item.href)
              : activeView === item.view;

          return (
            <button
              key={item.label}
              type="button"
              onClick={() =>
                isSettings
                  ? onOpenSettings()
                  : isPathItem
                    ? onNavigatePath(item.href)
                    : onNavigate(item.view)
              }
              aria-current={isActive ? "page" : undefined}
              className={cn(
                // task 2109 (mfh-tabbar-320): at <375px the four-column grid
                // leaves too little width for "Passport"/"Ask Sill" at the
                // default px-3/text-xs sizing — tighten padding and drop the
                // label a notch ONLY below 375px (390/400px untouched) so
                // every label stays on one line with no horizontal spill
                // into the button's own padding.
                "rounded-2xl px-3 py-3 text-center text-xs font-medium transition max-[374px]:px-1",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground",
              )}
            >
              <NavItemIcon icon={Icon} mobile />
              <span
                className="mt-1 block leading-tight whitespace-nowrap max-[374px]:text-[11px]"
                data-testid="mobile-tab-label"
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export function MobileSidebar({
  activeView,
  feedEnabled = false,
  marketplaceEnabled = false,
  isOpen,
  onClose,
  onNavigate,
  onNavigatePath,
  onOpenSettings,
}: {
  activeView: ShellActiveView | undefined;
  feedEnabled?: boolean;
  marketplaceEnabled?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (view: DashboardPrimaryView) => void;
  onNavigatePath: (path: string) => void;
  onOpenSettings: () => void;
}) {
  const currentLocation =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : "";
  const mobileMessagingEnabled = useMessagingEnabled();
  const messagesUnreadCount = useMessagesStore((s) => s.unreadCount);
  const offersCount = useOffersStore((s) => s.count);
  const mobileSidebarItems = getMobileSidebarItems(
    feedEnabled,
    marketplaceEnabled,
    mobileMessagingEnabled,
    messagesUnreadCount,
    offersCount,
  );
  const support = useSupport();

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 bg-foreground/12 lg:hidden"
        >
          <button
            type="button"
            aria-label="Close navigation menu"
            className="absolute inset-0"
            onClick={onClose}
          />

          <motion.aside
            id="mobile-sidebar"
            aria-label="Mobile sidebar"
            initial={{ x: -28, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -28, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative flex h-full w-[min(22rem,88vw)] flex-col border-r border-border bg-card shadow-xl"
          >
            <div className="flex h-14 items-center justify-between border-b border-border/70 px-4">
              <button
                type="button"
                onClick={() => {
                  onNavigate("overview");
                  onClose();
                }}
                aria-label="Go to Overview"
                className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <img src={sillLogo} alt="Sill" className="h-6" />
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close navigation menu"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition hover:bg-accent hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-1 flex-col justify-between px-3 py-4">
              <div className="space-y-1.5">
                <p className="px-2 pb-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  Dashboard pages
                </p>
                {mobileSidebarItems.map((item) => {
                  const isViewItem = item.status === "live" && "view" in item;
                  const isPathItem = item.status === "live" && "href" in item;
                  const isActionItem =
                    item.status === "live" && "action" in item;
                  const isActive = isViewItem
                    ? activeView === item.view
                    : isPathItem
                      ? isDashboardPathActive(
                          activeView,
                          currentLocation,
                          item.href,
                        )
                      : false;

                  return (
                    <SidebarStepperButton
                      key={item.label}
                      active={isActive}
                      disabled={item.status !== "live"}
                      icon={item.icon}
                      label={item.label}
                      badge={"badge" in item ? item.badge : undefined}
                      trailingIcon={item.trailingIcon}
                      onClick={
                        isViewItem
                          ? () => onNavigate(item.view)
                          : isPathItem
                            ? () => onNavigatePath(item.href)
                            : isActionItem && item.action === "feedback"
                              ? () => support.openFeedback()
                              : undefined
                      }
                    />
                  );
                })}
              </div>

              <div className="border-t border-border/70 pt-4">
                <SidebarStepperButton
                  active={activeView === "settings"}
                  icon={UserRound}
                  label="Account"
                  onClick={onOpenSettings}
                />
              </div>
            </div>
          </motion.aside>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export function SidebarStepperButton({
  active,
  badge,
  compact = false,
  disabled = false,
  icon: Icon,
  iconOnly = false,
  label,
  onClick,
  soon,
  testId,
  title: titleProp,
  trailingIcon: TrailingIcon,
}: {
  active: boolean;
  badge?: number;
  compact?: boolean;
  disabled?: boolean;
  icon?: DashboardNavIcon;
  iconOnly?: boolean;
  label: string;
  onClick?: () => void;
  soon?: boolean;
  testId?: string;
  title?: string;
  trailingIcon?: DashboardNavIcon;
}) {
  return (
    <button
      type="button"
      aria-current={active ? "page" : undefined}
      aria-label={iconOnly ? label : undefined}
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      title={iconOnly ? label : titleProp}
      className={cn(
        "flex min-w-0 w-full items-center justify-between overflow-hidden rounded-xl text-left transition hover:shadow-none",
        iconOnly
          ? "h-11 w-11 justify-center px-0 py-0"
          : compact
            ? "px-[7px] py-[4px]"
            : "px-[8px] py-[6px]",
        disabled && "cursor-default opacity-70",
        compact
          ? disabled
            ? "text-muted-foreground"
            : active
              ? "text-primary hover:bg-muted hover:text-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          : disabled
            ? "text-muted-foreground"
            : active
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
        iconOnly &&
          active &&
          "bg-primary/10 text-primary hover:bg-primary/12 hover:text-primary",
      )}
    >
      <span
        className={cn(
          "inline-flex min-w-0 items-center gap-2.5",
          iconOnly && "justify-center",
        )}
      >
        <NavItemIcon compact={compact} icon={Icon} />
        <span
          className={cn(
            "min-w-0 truncate font-medium",
            iconOnly && "sr-only",
            compact ? "text-[12px]" : "text-[13px]",
            compact
              ? disabled
                ? "text-muted-foreground"
                : active
                  ? "font-semibold text-primary"
                  : ""
              : disabled
                ? "text-muted-foreground"
                : active
                  ? "text-primary"
                  : "",
          )}
        >
          {label}
        </span>
      </span>
      {soon && !iconOnly ? (
        <span
          aria-label="Coming soon"
          className="ml-2 inline-flex shrink-0 items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Soon
        </span>
      ) : badge != null && !iconOnly ? (
        <span
          aria-label={`${badge} offer${badge === 1 ? "" : "s"}`}
          className="ml-2 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 px-1.5 text-[11px] font-semibold text-primary"
        >
          {badge}
        </span>
      ) : TrailingIcon ? (
        <span aria-hidden="true" className="ml-2 inline-flex shrink-0">
          <TrailingIcon className="h-3 w-3 fill-primary text-primary" />
        </span>
      ) : null}
    </button>
  );
}

function NavItemIcon({
  compact = false,
  icon: Icon,
  mobile = false,
}: {
  compact?: boolean;
  icon?: DashboardNavIcon;
  mobile?: boolean;
}) {
  if (!Icon) {
    return null;
  }

  return (
    <span className="inline-flex">
      <Icon
        className={cn(
          mobile ? "mx-auto h-4 w-4" : "h-3.5 w-3.5",
          compact && !mobile && "h-3 w-3",
        )}
      />
    </span>
  );
}

function DashboardBreadcrumbItem({
  current = false,
  dataSlot,
  item,
}: {
  current?: boolean;
  dataSlot?: string;
  item: DashboardHeaderBreadcrumbItem;
}) {
  const className = cn(
    "truncate rounded text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
    current
      ? "font-semibold tracking-[-0.01em] text-foreground"
      : "font-medium text-muted-foreground hover:text-foreground",
  );

  if (!current && item.onClick) {
    return (
      <button
        className={className}
        data-slot={dataSlot}
        onClick={item.onClick}
        type="button"
      >
        {item.label}
      </button>
    );
  }

  return (
    <span
      aria-current={current ? "page" : undefined}
      className={className}
      data-slot={dataSlot}
    >
      {item.label}
    </span>
  );
}

export function DashboardHeader({
  activeView,
  breadcrumbItems,
  feedUnreadCount,
  isMobileNavOpen,
  isMobileScoreOpen,
  onNavigateOverview,
  onOpenAdvisor,
  onToggleMobileNav,
  onToggleMobileScore,
  readiness,
  readinessRefreshing = false,
}: {
  activeView: ShellActiveView | undefined;
  breadcrumbItems?: readonly DashboardHeaderBreadcrumbItem[];
  /** Unread notification count for the bell badge. null/undefined = badge hidden. */
  feedUnreadCount?: number | null;
  isMobileNavOpen: boolean;
  isMobileScoreOpen: boolean;
  onNavigateOverview?: () => void;
  onOpenAdvisor: () => void;
  onToggleMobileNav: () => void;
  onToggleMobileScore: () => void;
  readiness?: ReadinessResult | null;
  readinessRefreshing?: boolean;
}) {
  const topBarLabel = getTopBarLabel(activeView);
  const resolvedBreadcrumbItems = breadcrumbItems ?? [{ label: topBarLabel }];
  const mobileTitle =
    resolvedBreadcrumbItems[resolvedBreadcrumbItems.length - 1]?.label ??
    topBarLabel;
  const mobileFactors = buildMobileReadinessFactors(readiness);
  const hasLiveReadiness = typeof readiness?.score === "number";
  // SILL-323 follow-up: when readiness exists but score is nulled by a
  // cap override, render the pill in the same "—" / "Not yet qualified"
  // shape the score panel uses. Without this branch the pill silently
  // hides (because hasLiveReadiness is false), leaving DNQ users with no
  // mobile-readiness affordance at all.
  const scoreIsCapped = readiness != null && readiness.score === null;
  const showMobileReadinessPill =
    hasLiveReadiness || scoreIsCapped || readinessRefreshing;
  return (
    <header className="-mx-4 -mt-5 h-14 bg-background sm:-mx-5 lg:-mx-6 lg:-mt-6 lg:h-auto">
      <div className="fixed left-0 right-0 top-0 z-30 bg-background lg:sticky lg:left-auto lg:right-auto lg:bg-background/92 lg:backdrop-blur-md">
        <div className="flex h-14 items-center justify-between gap-3 border-b border-border/80 px-4 sm:px-5 lg:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              type="button"
              aria-controls="mobile-sidebar"
              aria-expanded={isMobileNavOpen}
              aria-label="Open navigation menu"
              onClick={onToggleMobileNav}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition hover:bg-accent hover:text-foreground lg:hidden"
            >
              {isMobileNavOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <Menu className="h-4 w-4" />
              )}
            </button>

            <p
              data-slot="dashboard-mobile-title"
              className="truncate text-sm font-semibold tracking-[-0.01em] text-foreground lg:hidden"
            >
              {mobileTitle}
            </p>

            <nav
              aria-label="Dashboard breadcrumb"
              className="hidden min-w-0 items-center gap-2 lg:flex"
            >
              <DashboardBreadcrumbItem
                dataSlot="dashboard-breadcrumb-root"
                item={{ label: "Dashboard", onClick: onNavigateOverview }}
              />
              {resolvedBreadcrumbItems.map((item, index) => (
                <span className="contents" key={`${item.label}-${index}`}>
                  <ChevronRight
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0 text-muted-foreground/70"
                  />
                  <DashboardBreadcrumbItem
                    current={index === resolvedBreadcrumbItems.length - 1}
                    dataSlot={
                      index === resolvedBreadcrumbItems.length - 1
                        ? "dashboard-breadcrumb-current"
                        : undefined
                    }
                    item={item}
                  />
                </span>
              ))}
            </nav>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {showMobileReadinessPill ? (
              <button
                type="button"
                aria-label="Toggle readiness score breakdown"
                aria-description={scoreIsCapped ? "Not yet qualified" : readiness?.label}
                className="inline-flex min-h-10 items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1.5 text-sm font-semibold text-primary lg:hidden"
                onClick={onToggleMobileScore}
                aria-expanded={isMobileScoreOpen}
              >
                <span className="rounded-full bg-primary px-2 py-1 text-primary-foreground">
                  {scoreIsCapped ? (
                    "—"
                  ) : hasLiveReadiness ? (
                    readiness.score
                  ) : (
                    <span className="block h-4 w-7 animate-pulse rounded-full bg-primary-foreground/70 motion-reduce:animate-none" />
                  )}
                </span>
                <span className="hidden min-[480px]:inline">
                  {scoreIsCapped
                    ? "Not yet qualified"
                    : (readiness?.label ?? "Updating")}
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform",
                    isMobileScoreOpen && "rotate-180",
                  )}
                />
              </button>
            ) : null}

            <DashboardHeaderBadges feedUnreadCount={feedUnreadCount} />

            <AskSillHeaderToggle onOpen={onOpenAdvisor} />
          </div>
        </div>

        <AnimatePresence initial={false}>
          {isMobileScoreOpen ? (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="px-4 pb-3 sm:px-5 lg:hidden"
            >
              <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                {mobileFactors.length > 0 ? (
                  <div className="space-y-3">
                    {mobileFactors.map((item) => (
                      <div
                        key={item.id}
                        className="rounded-2xl bg-muted/60 px-4 py-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-medium text-foreground">
                            {item.label}
                          </p>
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {item.confidence}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl bg-muted/60 px-4 py-3">
                    <p className="text-sm font-medium text-foreground">
                      {readinessRefreshing
                        ? "Loading live readiness"
                        : "No live readiness score yet"}
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Finish onboarding to unlock the score, factor breakdown,
                      and plan.
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  className="mt-4 inline-flex w-full items-center justify-center rounded-2xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground"
                  onClick={onOpenAdvisor}
                >
                  Get coaching on my score
                </button>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </header>
  );
}

function AvatarBadge({
  firstName,
  large = false,
  onClick,
}: {
  firstName: string;
  large?: boolean;
  onClick?: () => void;
}) {
  const className = cn(
    "inline-flex items-center justify-center rounded-full bg-primary/10 font-semibold text-primary",
    "transition-[background-color,color,scale] duration-200 ease-[cubic-bezier(0.2,0,0.13,1)] hover:bg-primary/15 active:scale-[0.96]",
    large ? "h-10 w-10 text-sm" : "h-10 w-10 text-sm",
  );
  const initials = getUserInitials(firstName);

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={className}
        aria-label="Open account settings"
      >
        {initials}
      </button>
    );
  }

  return <div className={className}>{initials}</div>;
}

function getTopBarLabel(activeView: ShellActiveView | undefined) {
  if (activeView === "passport") {
    return "Passport";
  }

  if (activeView === "readiness") {
    return "Readiness";
  }

  if (activeView === "properties") {
    return "Properties";
  }

  if (activeView === "calculators") {
    return "Calculators";
  }

  if (activeView === "automation") {
    return "Automation";
  }

  if (activeView === "help") {
    return "Help Center";
  }

  if (activeView === "offers") {
    return "Your Offers";
  }

  if (activeView === "marketplace-browse") {
    return "Browse agents";
  }

  if (activeView === "documents") {
    return "Documents";
  }

  if (activeView === "feed") {
    return "Feed";
  }

  if (activeView === "rate-watch") {
    return "Rate Watch";
  }

  if (activeView === "advisor") {
    return "Ask Sill";
  }

  if (activeView === "settings") {
    return "Account";
  }

  if (activeView === "publish") {
    return "Publish";
  }

  if (activeView === "team") {
    return "Your Team";
  }

  // The thread surfaces highlight the sidebar "Messages" item (see
  // DashboardShell's /threads branch); without this case the mobile top bar
  // fell through to "Overview" while the screen showed Messages.
  if (activeView === "messages") {
    return "Messages";
  }

  return "Overview";
}

function mapSavedPropertyToTrackedProperty(
  property: SavedProperty,
  readinessPreview?: SavedPropertyReadinessPreview | null,
): TrackedProperty {
  const locationBits = [
    property.city,
    property.state,
    property.postal_code,
  ].filter((value): value is string => Boolean(value));
  return {
    id: property.id,
    address: property.address_line1,
    cityMeta:
      locationBits.length > 0 ? locationBits.join(", ") : "Saved property",
    listPrice: property.list_price,
    monthlyPayment: null,
    readinessLabel: readinessPreview?.label ?? null,
    readinessScore: readinessPreview?.score ?? null,
    visualToken: property.visual_token,
  };
}

/**
 * The header "Ask Sill" pill is a TOGGLE for the drawer (2026-10-08): click
 * opens it (through the caller's `onOpenAdvisor`, which pairs the open with
 * its analytics), click again closes it — the same `closeDrawer` the
 * drawer's own X uses, so the close analytics fire unchanged. While open it
 * drops from solid to the soft "pressed" tone and reports `aria-expanded`,
 * so the pill visibly owns the panel beside it.
 */
function AskSillHeaderToggle({ onOpen }: { onOpen?: () => void }) {
  const drawerOpen = useAdvisorDrawerStore((s) => s.drawerOpen);
  const closeDrawer = useAdvisorDrawerStore((s) => s.closeDrawer);
  return (
    <button
      aria-expanded={drawerOpen}
      className={cn(
        "hidden items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold transition lg:inline-flex",
        drawerOpen
          ? "bg-primary-soft text-primary ring-1 ring-inset ring-primary/35 hover:bg-primary-soft/70"
          : "bg-primary text-primary-foreground shadow-sm hover:bg-primary/92",
      )}
      data-slot="ask-sill-header-toggle"
      // Read the store at click time, not the render-time `drawerOpen`, so
      // a click can never act on a stale render's idea of open/closed.
      onClick={() =>
        useAdvisorDrawerStore.getState().drawerOpen ? closeDrawer() : onOpen?.()
      }
      type="button"
    >
      <MessageSquareText className="h-4 w-4" />
      Ask Sill
    </button>
  );
}
